<template>
  <div class="bg-white p-0 md:p-8 space-y-8">
    <div class="flex items-start">
      <div class="flex items-center gap-4 mr-2.5">
        <div class="w-10 h-10 rounded-lg border border-zinc-200 flex items-center justify-center">
          <span class="text-lg font-semibold text-gray-700">Q{{ questionNumber }}</span>
        </div>
      </div>
      <span class="items-center text-lg text-zinc-800 font-medium px-2 py-3 rounded-lg"
            :class="question.user_answer?.score > 0 ? 'bg-success/10' : 'bg-error/10'"
      >
        {{ __('Gain:') }} {{ question.user_answer?.score || 0 }} {{ __('points') }}
      </span>
    </div>
    <div class="text-zinc-800 text-base font-medium">{{ question.question.title }}</div>
    <div class="text-zinc-800 text-2xl font-medium">{{ __('Answer:') }}</div>
    <div class="space-y-6">
      <div v-if="isChoiceQuestion" class="space-y-6">
        <div
            v-for="answer in question.challenge_answers"
            :key="answer.id"
            class="relative p-4 md:p-6 rounded-lg border-2 transition-all flex items-center gap-3"
            :class="getAnswerClass(answer)"
        >
          <div class="flex-shrink-0">
            <div
                v-if="isUserSelected(answer.id)"
                class="w-5 h-5 rounded border-2 border-zinc-600 bg-white flex items-center justify-center"
            >
              <Icon name="check" class="w-4 h-4 !text-base items-center justify-center text-zinc-600" />
            </div>
            <div v-else class="w-5 h-5 rounded border-2 border-zinc-600"></div>
          </div>

          <span class="text-gray-700 text-base font-medium flex-1">{{ answer.text }}</span>

          <div v-if="isUserSelected(answer.id) && answer.is_correct"
               class="absolute top-0 -right-0.5 w-6 h-6 bg-success flex items-center justify-center rounded-tr-lg rounded-bl-lg">
            <Icon name="check" class="w-4 h-4 text-white !text-lg" />
          </div>
          <div v-else-if="isUserSelected(answer.id) && !answer.is_correct"
               class="absolute top-0 -right-0.5 w-6 h-6 bg-error flex items-center justify-center rounded-tr-lg rounded-bl-lg">
            <Icon name="close" class="w-4 h-4 text-white !text-lg" />
          </div>
        </div>
      </div>

      <div v-else class="space-y-4">
        <div class="space-y-2">
          <p class="text-gray-700 text-base">
            {{ question.user_answer?.text_answer || __('No answer provided') }}
          </p>
        </div>

        <div v-if="question.user_answer?.feedback ">
          <div class="text-sm text-gray-600 rounded-xl border-zinc-400 border p-4">
            <div class="flex items-center gap-4">
              <img
                  class="h-11 w-11 rounded-full ring-2 ring-gray-200 self-start"
                  :src="question.recruiter.profile_photo"
                  :alt="question.recruiter.full_name"
              />
              <div class="flex flex-col items-start gap-1">
                <span class="text-xs text-zinc-800 font-medium">
                  {{ question.recruiter.full_name }}
                </span>
                <span class="text-base text-zinc-600">
                  {{ question.user_answer.feedback }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!isMobile" class="flex justify-between !mt-14">
      <button
          v-if="!isFirstQuestion"
          type="button"
          @click="$emit('previous')"
          class="inline-flex items-center px-6 py-2 hover-lift-no-shadow text-gray-700 hover:bg-gray-50 hover:rounded-md transition-colors"
      >
        <Icon name="arrow_back_ios" class="w-5 h-5 text-xl text-zinc-800" />
        {{ __('Back to previous question') }}
      </button>
      <div class="ml-auto flex gap-3">
        <button
            v-if="!isLastQuestion"
            @click="$emit('next')"
            type="button"
            class="px-6 py-2 rounded-md transition-colors bg-primary text-white hover:bg-primary/90"
        >
          <span>{{ __('Next') }}</span>
        </button>
        <button
            v-else
            @click="$emit('done')"
            type="button"
            class="px-6 py-2 rounded-md transition-colors bg-primary text-white hover:bg-primary/90"
        >
          <span>{{ __('Done') }}</span>
        </button>
      </div>
    </div>

    <div v-else class="flex flex-col gap-2 w-full !mt-14">
      <button
          v-if="!isFirstQuestion"
          type="button"
          @click="$emit('previous')"
          class="w-full px-4 py-3 text-gray-700 hover:bg-gray-100 transition-colors flex items-center justify-center gap-2 rounded"
      >
        <Icon name="arrow_back_ios" class="w-4 h-4" />
        {{ __('Back to previous question') }}
      </button>

      <button
          v-if="!isLastQuestion"
          @click="$emit('next')"
          type="button"
          class="w-full px-4 py-3 rounded-md bg-primary text-white hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
      >
        {{ __('Next') }}
        <Icon name="arrow_forward_ios" class="w-4 h-4" />
      </button>
      <button
          v-if="isLastQuestion && !isFirstQuestion"
          @click="$emit('done')"
          type="button"
          class="w-full px-4 py-3 rounded-md bg-primary text-white hover:bg-primary/90 transition-colors"
      >
        {{ __('Done') }}
      </button>

      <button
          v-if="isLastQuestion && isFirstQuestion"
          @click="$emit('done')"
          type="button"
          class="w-full px-4 py-3 rounded-md bg-primary text-white hover:bg-primary/90 transition-colors"
      >
        {{ __('Done') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import {computed, onBeforeUnmount, onMounted, ref} from 'vue';
import { Icon } from '@/components/block/icon';
import { __ } from '@/helpers';

const props = defineProps({
  question: {
    type: Object,
    required: true
  },
  questionNumber: {
    type: Number,
    required: true
  },
  isFirstQuestion: {
    type: Boolean,
    default: false
  },
  isLastQuestion: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['next', 'previous', 'done']);

const windowWidth = ref(window.innerWidth);

const isMobile = computed(() => windowWidth.value < 640);

const handleResize = () => {
  windowWidth.value = window.innerWidth;
};

onMounted(() => {
  window.addEventListener("resize", handleResize);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);
});

const isChoiceQuestion = computed(() => {
  const answerType = props.question.question.answer_type;
  return answerType === 1 || answerType === 2; // Single choice or Multiple choice
});

const isUserSelected = (answerId) => {
  if (!props.question.user_answer) return false;
  const answerIds = props.question.user_answer.answer_ids;
  if (!answerIds) return false;
  return Array.isArray(answerIds) ? answerIds.includes(answerId) : false;
};

const getAnswerClass = (answer) => {
  const selected = isUserSelected(answer.id);
  const isReviewMode = props.question.user_answer?.reviewed_at !== null;

  if (selected && answer.is_correct) {
    return 'border-success bg-success/10';
  } else if (selected && !answer.is_correct) {
    return 'border-error bg-white';
  } else if (!selected && answer.is_correct && isReviewMode) {
    return 'border-success bg-white';
  }

  return 'border-gray-200 bg-white';
};
</script>